import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography, Stack } from "@mui/material";
import { useBillingCompanyContext } from "../../../../../providers";
import { api } from "../../../../../api/api";
import { StandardCard } from "../../../../../components/Cards";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAuth } from "../../../../../contexts/AuthProvider";
import { config } from "../../../../../config";


export const WhatsappQrNotActive: React.FC<{}> = () => {
    const companyOutlet = useBillingCompanyContext();
    const { token, user, fetchUserProfile } = useAuth();
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const [qrImage, setQrImage] = useState<string | null>(null);
    const [status, setStatus] = useState<"loading" | "waiting" | "connected">("loading");
    const [connectedName, setConnectedName] = useState("");

    useEffect(() => {
        const checkConnectionStatus = async () => {
            try {
                const response = await api.get(`/baileywhatsapp/status`, {
                    params: {
                        reference_column_uuid: user.userUuid,
                        baseURL: `${config.api.baseUrl}/baileywhatsapp/incoming-bailey-messages`
                    },
                });

                const data = response.data;

                if (data.connectionStatus === "Connected" && !data.hasQR) {
                    setStatus("connected");
                    setConnectedName(data.connectedName || data.connectedPhone || "");
                    setQrImage(null);
                    fetchUserProfile();

                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }

                } else if (data.hasQR && data.qr) {
                    setStatus("waiting");
                    setQrImage(data.qr);
                } else {
                    setStatus("waiting");
                }
            } catch (error) {
                console.error("Error checking WhatsApp connection status:", error);
            }
        };

        checkConnectionStatus();
        pollingIntervalRef.current = setInterval(checkConnectionStatus, 3000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [companyOutlet.comapny.company_outlet_uuid]);

    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f0f2f5",
                height: "100%",
                borderBottom: "6px solid #43c4a0",
            }}
        >
            {status === "loading" && (
                <Stack alignItems="center" spacing={2} py={6}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" color="text.secondary">
                        Checking WhatsApp connection...
                    </Typography>
                </Stack>
            )}

            {status === "waiting" && (
                <Stack alignItems="center" spacing={2} py={4}>
                    <Typography variant="h5" fontWeight={700}>
                        Scan QR Code to Connect WhatsApp
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
                    </Typography>
                    {qrImage ? (
                        <Box
                            component="img"
                            src={qrImage}
                            alt="WhatsApp QR Code"
                            sx={{
                                width: 280,
                                height: 280,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                mt: 2,
                            }}
                        />
                    ) : (
                        <Stack alignItems="center" spacing={1} py={4}>
                            <CircularProgress size={30} />
                            <Typography variant="body2" color="text.secondary">
                                Generating QR code...
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}

            {status === "connected" && (
                <Stack alignItems="center" spacing={2} py={6}>
                    <CheckCircleIcon sx={{ fontSize: 60, color: "success.main" }} />
                    <Typography variant="h5" fontWeight={700} color="success.main">
                        WhatsApp Connected
                    </Typography>
                    {connectedName && (
                        <Typography variant="body1" color="text.secondary">
                            Connected as: {connectedName}
                        </Typography>
                    )}
                </Stack>
            )}
        </Box>
    );
};
